<script lang="ts">
	import { useQueryClient } from '@tanstack/svelte-query';
	import { setContext } from 'svelte';
	import { derived, type Readable } from 'svelte/store';
	import { PrimaryMixpanelEvents } from '../../constants/mixpanel-events';
	import { PrimaryRoutes } from '../../constants/routes';
	import { createPrimaryQuery } from '../../utils/createPrimaryQuery/createPrimaryQuery';
	import { createPrimaryCartQuery } from '../../utils/getPrimaryCartItems/getPrimaryCartItems';
	import { getPrimaryCartItemsFromOrder } from '../../utils/getPrimaryCartItemsFromQuery/getPrimaryCartItemsFromQuery';
	import { Contexts } from '#lib/constants/contexts.js';
	import { FeatureFlags } from '#lib/constants/feature-flags.js';
	import { CartPlatform } from '#lib/features/checkout/constants/cart-plaforms.js';
	import { createCartIconContext } from '#lib/features/checkout/utils/createCartIconContext/index.js';
	import type { CartItems } from '#lib/features/checkout/utils/createCartItemsStore/index.js';
	import { createCheckoutContext } from '#lib/features/checkout/utils/createCheckoutContext/index.js';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { createTimeContext } from '#lib/features/time/utils/createTimeContext/createTimeContext.js';
	import { createWeb3Context } from '#lib/features/web3/utils/createWeb3Context/index.js';
	import { createWishlistContext } from '#lib/features/wishlist/utils/createWishlistContext/index.js';
	import { copyCartItemsForDrop } from '#lib/websites/primary/pages/PrimaryCheckoutPage/utils/copyCartItemsForDrop/index.js';
	import { page } from '$app/state';

	interface Props {
		children?: import('svelte').Snippet;
	}

	let { children }: Props = $props();

	const key = 'primary';

	const queryClient = useQueryClient();

	setContext(Contexts.Now, createTimeContext());

	if (
		page.route.id?.startsWith('/primary/checkout') &&
		page.data.dropPageSlug &&
		page.data.dropCode &&
		!page.data.isGeneric
	) {
		copyCartItemsForDrop(page.data.dropPageSlug, page.data.dropCode);
	}

	setContext(
		Contexts.Checkout,
		createCheckoutContext({
			key,
			cartEntryClasses: {},
			mixpanelEvents: PrimaryMixpanelEvents,
			showZeroPence: false,
			codeEnabled: false,
			checkoutRoute: PrimaryRoutes.Checkout,
			homeRoute: GlobalRoutes.PrimaryHome,
			browseButtonRoute: '',
			browseButtonLabel: 'Browse Artworks',
			orderConfirmedRoute: PrimaryRoutes.OrderConfirmed,
			platform: CartPlatform.Primary,
			createItemsQuery: createPrimaryQuery,
			createCartItemsQuery: (
				cartItems: Readable<CartItems | undefined>,
				defaultLoading?: boolean
			) => {
				const query = createPrimaryCartQuery(
					queryClient,
					cartItems,
					defaultLoading
				);
				return derived(query, ($query) => ({
					isFetching: $query.isPending || $query.isLoading,
					error: $query.error,
					formattedItems: $query.formattedItems,
					subtotal: $query.subtotal,
				}));
			},
			getCartItemsFromOrder: getPrimaryCartItemsFromOrder,
		})
	);

	setContext(Contexts.ShowCartCheckout, createCartIconContext());
	setContext(
		Contexts.Web3,
		createWeb3Context(false, 'To support your application')
	);

	// The wishlist is cross-drop, so its context lives at the primary layout level
	// (set once) rather than in the per-drop checkout override, which would
	// re-instantiate the store with a drop-scoped key on each drop.
	if (FeatureFlags.Wishlist) {
		setContext(Contexts.Wishlist, createWishlistContext());
	}
</script>

{@render children?.()}
